/* eslint-disable camelcase */
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createSelector, createStructuredSelector } from 'reselect';
import { superChatSelector } from 'ai/selectors/superChatSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import { gladChatSelector } from 'selectors/gladChat/gladChatSelector';
import SuperChatActions from 'ai/actions/superChatActions';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import sdnApi from 'ai/services/sdn';
import { getGENAISession, setGENAISession } from 'ai/utils/sessionStorage';
import { getAnonymousId } from 'ai/utils/aiBeautyChat';
import store from 'store/Store';
import watch from 'redux-watch';
import { NAV_SOURCES } from 'ai/analytics/events';
import SuperChatEN from 'ai/components/SuperChat/locales/SuperChat_en_US';
import SuperChatFR from 'ai/components/SuperChat/locales/SuperChat_fr_CA';

const { wrapHOC, wrapHOCComponent } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(SuperChatEN, SuperChatFR);

const { previousConversations } = sdnApi;

const localization = createStructuredSelector({
    title: getTextFromResource(getText, 'title')
});

const functions = dispatch => ({
    openSuperChat: (entryPoint, prompts = []) => {
        entryPoint.source = NAV_SOURCES.BOTTOM_NAV;
        dispatch(SuperChatActions.showSuperChat(true, prompts, entryPoint));
    }
});

const fields = createSelector(superChatSelector, localization, coreUserDataSelector, gladChatSelector, (superChat, locales, user, gladChat) => {
    return {
        localization: locales,
        user,
        config: superChat.config,
        contextPrompts: superChat.contextPrompts || [],
        isGladlyOpen: gladChat?.isChatOpen || false,
        ...superChat
    };
});

const withMinimizedSuperChatProps = compose(
    wrapHOC(connect(fields, functions)),
    wrapHOC(WrappedComponent => {
        class MinimizedSuperChatProps extends React.Component {
            state = {
                session: null,
                location: null
            };

            constructor(props) {
                super(props);
            }

            showMinimizedSuperChat() {
                const session = getGENAISession();
                const userBiId = this.props.user?.biId;
                const anonymousId = getAnonymousId(userBiId);

                if (session) {
                    this.setState({ session: session });

                    return;
                }

                if (userBiId) {
                    previousConversations(userBiId, anonymousId)
                        .then(data => {
                            const lastMessage = data?.messages?.at();
                            const sessionData = data?.sessions?.find(s => s.session_id === lastMessage?.session_id);

                            if (sessionData && !sessionData.expired) {
                                this.setState({ session: sessionData });

                                // Sanitize products to correct format for NEW_CONNECTION_REQUEST
                                // API returns full product details (id, url, image_url, etc.)
                                // but backend expects minimal format (product_id, sku_id)
                                const sanitizedSessionData = { ...sessionData };

                                if (sessionData.products?.length > 0) {
                                    sanitizedSessionData.products = sessionData.products.map(product => ({
                                        product_id: product.id || product.product_id,
                                        sku_id: product.default_sku || product.sku_id
                                    }));
                                }

                                setGENAISession({
                                    sessionId: sessionData.session_id,
                                    clientId: userBiId,
                                    anonymousId: anonymousId,
                                    ...sanitizedSessionData
                                });
                            }
                        })
                        .catch(_ => {
                            this.setState({ session: null });
                        });
                }
            }

            componentDidUpdate(prevProps) {
                if (prevProps.showSuperChat !== this.props.showSuperChat) {
                    this.showMinimizedSuperChat();
                }

                if (prevProps.user?.biId !== this.props.user?.biId) {
                    this.showMinimizedSuperChat();
                }
            }

            componentDidMount() {
                const watchLocation = watch(store.getState, 'historyLocation');
                this.showMinimizedSuperChat();

                store.subscribe(
                    watchLocation(location => {
                        this.setState({ location });
                    })
                );
            }

            render() {
                const { session, location } = this.state;

                return (
                    <WrappedComponent
                        {...this.props}
                        session={session}
                        location={location}
                    />
                );
            }
        }

        return wrapHOCComponent(MinimizedSuperChatProps, 'MinimizedSuperChatProps', [WrappedComponent]);
    })
);

export {
    withMinimizedSuperChatProps, fields, functions
};
